import { Component, computed, effect, inject, signal, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService, SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { createDynamicFilterState, DynamicFilterConfig, DynamicFilterValues, EnumValues, ModalService } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { ProductDevelopmentAdminGroup, ProductDevelopmentViewerGroup } from '../../models/authorization/enum/user-group.enum';
import { Yarn, YarnSearchRequest } from '../../models/yarns/yarn';
import { ProductDevelopmentStatus } from '../../models/enums/product-development-status';
import { YarnProxyService } from '../../proxies/item/yarn.proxy.service';
import { YarnDetailsModalComponent } from '../modals/yarn-details-modal/yarn-details-modal.component';
import { EnumService } from '../../services/enum.service';

@Component({
  selector: 'app-yarn',
  templateUrl: './yarn.component.html',
  styleUrl: './yarn.component.scss',
})
export class YarnComponent implements OnDestroy {
  private yarnProxyService = inject(YarnProxyService);
  private messageService = inject(MessageService);
  private modalService = inject(ModalService);
  private confirmationService = inject(ConfirmationService);
  private translateService = inject(TranslateService);
  private enumService = inject(EnumService);

  private destroy$ = new Subject<void>();
  private lastSearchCriteria: YarnSearchRequest = {};

  isLoading = signal<number>(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({
    storageKey: 'yarn-filters',
    config: computed(() => (this.enumsReady() ? this.filterConfig() : null)),
  });
  showFilters = signal<boolean>(true);

  // Pagination
  currentPage = signal(1);
  pageSize = signal(25);
  totalRecords = signal(0);

  // Sorting
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  // Data
  yarns = signal<Yarn[]>([]);
  statuses = signal<EnumValues>({});
  enumsReady = signal(false);

  getRowData(rowData: unknown): Yarn {
    return rowData as Yarn;
  }

  loadStatuses = effect(
    () => {
      const statusesValue = this.enumService.getEnum('ProductDevelopmentStatus', 'itemEnums').values;
      if (Object.keys(statusesValue).length > 0) {
        this.statuses.set(statusesValue);
        this.enumsReady.set(true);
      }
    },
    { allowSignalWrites: true },
  );

  translateFilter = effect(
    () => {
      if (this.translateService.currentLang) {
        this.tryInitializeFilterConfig();
      }
    },
    { allowSignalWrites: true },
  );

  ProductDevelopmentStatus = ProductDevelopmentStatus;

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));
  }

  canEdit() {
    // if Admin
    if (AuthService.hasRole(ProductDevelopmentAdminGroup.YarnAdmin)) {
      return true;
    }
    // if Viewer
    if (AuthService.hasRole(ProductDevelopmentViewerGroup.YarnViewer)) {
      return false;
    }
    // Default
    return false;
  }

  isReadyForProduction(yarn: Yarn): boolean {
    return yarn.status === ProductDevelopmentStatus.ReadyForProduction;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const statusOptions = Object.entries(this.statuses()).map(([key, value]) => ({
      value: key,
      label: value,
    }));

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'code',
          label: translate('Yarn_Code', 'Code'),
          placeholder: translate('Enter_yarn_code', 'Enter yarn code'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'longDescription',
          label: translate('Yarn_Description', 'Description'),
          placeholder: translate('Enter_yarn_description', 'Enter description'),
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: translate('Yarn_Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: statusOptions,
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  onSearch(searchCriteria: YarnSearchRequest): void {
    this.lastSearchCriteria = searchCriteria;
    this.startLoading();
    this.yarnProxyService
      .search(searchCriteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.yarns.set(response?.data?.items || []);
          this.totalRecords.set(response?.data?.totalCount || 0);
        },
        error: () => {
          this.yarns.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page || 0) + 1);
    this.pageSize.set(event.rows || this.pageSize());
    this.onSearch(this.lastSearchCriteria);
  }

  onSort(event: SortEvent): void {
    this.sortField.set(event.field);
    this.sortOrder.set(event.order ?? -1);
    this.currentPage.set(1);
    this.onSearch(this.lastSearchCriteria);
  }

  private searchWithFilters(values: DynamicFilterValues): void {
    const filters = values as YarnSearchRequest;
    const searchCriteria: YarnSearchRequest = {
      ...filters,
      statuses: filters.statuses?.map((status) => Number(status)),
    };
    this.currentPage.set(1);
    this.onSearch(searchCriteria);
  }

  openYarnModal(yarn?: Yarn) {
    this.modalService
      .open(
        YarnDetailsModalComponent,
        { modalClass: 'modal_small' },
        {
          yarn: yarn,
          canEdit: this.canEdit(),
        },
      )
      .subscribe((result) => {
        if (result && result.action === 'save' && result.data) {
          this.onSearch(this.lastSearchCriteria);
        }
      });
  }

  deletePopup(event: Event, yarn: Yarn) {
    event.stopPropagation();
    this.confirmationService.confirm({
      key: 'deleteYarn',
      target: event.target as EventTarget,
      message: `${this.translateService.instant('Yarn_Delete_Body_Dialog')} ${yarn.code}?`,
      header: this.translateService.instant('"Common_Header_Dialog_Danger'),
      icon: 'pi pi-info-circle',
      rejectLabel: this.translateService.instant('Common_Cancel'),
      rejectButtonProps: {
        label: this.translateService.instant('Common_Cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.translateService.instant('Common_Delete'),
        severity: 'danger',
      },

      accept: () => {
        this.deleteYarn(yarn);
      },
    });
  }

  deleteYarn(yarn: Yarn) {
    this.yarnProxyService.updateYarn({ ...yarn, status: ProductDevelopmentStatus.Inactive }).subscribe({
      next: (response) => {
        if (response.success) {
          this.onSearch(this.lastSearchCriteria);

          this.messageService.add({
            severity: 'success',
            summary: `Yarn ${yarn?.code} Deleted`,
            key: 'br',
            life: 2500,
          });
        }
      },
    });
  }
}
